/**
 * Layout coordinate loading (doc 147 §9 Phase 2, PERF-03).
 *
 * The server pre-computes ForceAtlas2 coordinates and stores them alongside the
 * manifest; the client just fetches the JSON. `normalizeLayoutCoords` is the
 * pure, unit-tested validator; the Storage fetch around it is a thin wrapper.
 */
import { ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase/client.js';
import { gsPathFromUrl } from '../firebase/firestore.js';
import type { LayoutCoords } from './buildGraph.js';

/** Coerce an untrusted layout document into valid {@link LayoutCoords}. */
export function normalizeLayoutCoords(raw: unknown): LayoutCoords {
  if (typeof raw !== 'object' || raw === null) return {};
  const coords: LayoutCoords = {};
  for (const [id, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value !== 'object' || value === null) continue;
    const v = value as Record<string, unknown>;
    if (typeof v.x === 'number' && typeof v.y === 'number' &&
        Number.isFinite(v.x) && Number.isFinite(v.y)) {
      coords[id] = { x: v.x, y: v.y };
    }
  }
  return coords;
}

/* c8 ignore start — thin Storage SDK wrapper; emulator/manual tested. */

/**
 * Fetch + normalize the pre-computed layout from its Cloud Storage URL.
 * Returns `{}` on any failure — a missing layout just means the client falls
 * back to the deterministic circular layout in `buildGraph`.
 */
export async function fetchLayout(layoutUrl: string): Promise<LayoutCoords> {
  try {
    const path = gsPathFromUrl(layoutUrl);
    const downloadUrl = await getDownloadURL(ref(storage, path));
    const res = await fetch(downloadUrl);
    if (!res.ok) return {};
    return normalizeLayoutCoords(await res.json());
  } catch {
    return {};
  }
}

/* c8 ignore stop */
